import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { TEMPLATE_TYPE, useTemplateOptions } from '#/entities/template'
import {
  insertFieldsToSlashGroups,
  useTemplateVariableOptions,
} from '#/entities/template-variable'
import { formatUsPhone } from '#/shared/lib/format'
import { PhoneEditor } from '#/shared/ui/phone-editor'

import type { PanelState } from './panel-state'
import { SmsRecipientsSummary } from './recipients-summary'

interface PhoneTabProps {
  phone: PanelState['phone']
  shipmentId: string | undefined
  compact?: boolean
}

export function PhoneTab({ phone, shipmentId, compact }: PhoneTabProps) {
  const { t } = useTranslation()
  const { options: templateOptions, isLoading: templatesLoading } =
    useTemplateOptions({ templateType: TEMPLATE_TYPE.sms })
  const { groups: insertFieldGroups, insertValues } =
    useTemplateVariableOptions(shipmentId)
  const slashGroups = useMemo(
    () => insertFieldsToSlashGroups(insertFieldGroups, insertValues),
    [insertFieldGroups, insertValues],
  )
  const fromOptions = phone.fromNumbers.map((value) => ({
    value,
    label: formatUsPhone(value) || value,
  }))
  const recipients = phone.toPhones.map((value) => ({
    id: value,
    label: formatUsPhone(value) || value,
  }))
  return (
    <PhoneEditor
      compact={compact}
      showSmsSegments
      enableVoice
      from={phone.fromPhone}
      fromOptions={fromOptions}
      onFromChange={phone.setFromPhone}
      recipients={recipients}
      onAddRecipient={phone.addPhone}
      onRemoveRecipient={phone.removePhone}
      recipientsSummary={
        <SmsRecipientsSummary
          contacts={phone.toPhones}
          fromPhoneId={phone.fromPhoneId}
          message={phone.plainBody}
          compact={compact}
        />
      }
      enableCc={false}
      enableBcc={false}
      enableUpload={false}
      templateOptions={templateOptions}
      templateLoading={templatesLoading}
      onTemplateSelect={(id) => void phone.applyTemplate(id, insertValues)}
      slashGroups={slashGroups}
      value={phone.value}
      onChange={phone.onChange}
      onCancel={phone.onCancel}
      onSave={(html) => phone.onSend(html, insertValues)}
      saveLabel={
        phone.isSending ? t('editor.phone.sending') : t('editor.phone.save')
      }
    />
  )
}
